// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { MedplumClient } from '@medplum/core';
import type { Communication } from '@medplum/fhirtypes';
import { IN_APP_TAG, isRead, NOTIFICATION_READ_AT_URL, NOTIFICATION_TAG } from '@polarissleep/shared';

export interface SearchNotificationsOptions {
  /** Page offset (0-indexed). */
  offset?: number;
  /** Page size. */
  count?: number;
}

/**
 * Search Communication resources representing in-app notifications addressed to
 * the given recipient (Patient or Practitioner), sorted by send date descending.
 */
export async function searchNotifications(
  medplum: MedplumClient,
  recipientRef: string,
  options: SearchNotificationsOptions = {}
): Promise<Communication[]> {
  const { offset = 0, count = 20 } = options;
  return medplum.searchResources('Communication', [
    ['status', 'completed'],
    ['_tag', NOTIFICATION_TAG],
    ['_tag', IN_APP_TAG],
    ['recipient', recipientRef],
    ['_sort', '-sent'],
    ['_count', count.toString()],
    ['_offset', offset.toString()],
  ]);
}

/**
 * Count unread in-app notifications for the recipient. Caps the search at `cap`
 * results — if we hit the cap the UI should display "{cap}+".
 */
export async function countUnreadNotifications(
  medplum: MedplumClient,
  recipientRef: string,
  cap = 50
): Promise<number> {
  const results = await searchNotifications(medplum, recipientRef, { count: cap });
  return results.filter((c) => !isRead(c)).length;
}

/**
 * Mark a notification as read by adding the Polaris readAt extension. Idempotent —
 * returns the input unchanged if it is already marked read.
 */
export async function markNotificationRead(
  medplum: MedplumClient,
  communication: Communication
): Promise<Communication> {
  if (isRead(communication)) {
    return communication;
  }
  const updated: Communication = {
    ...communication,
    extension: [
      ...(communication.extension ?? []),
      {
        url: NOTIFICATION_READ_AT_URL,
        valueDateTime: new Date().toISOString(),
      },
    ],
  };
  return medplum.updateResource(updated);
}

/**
 * Mark every in-app notification for the recipient as read. Returns the number of
 * notifications updated.
 */
export async function markAllNotificationsRead(medplum: MedplumClient, recipientRef: string): Promise<number> {
  const all = await searchNotifications(medplum, recipientRef, { count: 100 });
  const unread = all.filter((c) => !isRead(c));
  await Promise.all(unread.map((c) => markNotificationRead(medplum, c)));
  return unread.length;
}

/**
 * Mark every child Communication of a thread as read for the given recipient.
 * Reuses the same notification-readAt extension used for in-app notifications —
 * a Communication is "read" when this extension is present. Idempotent: skips
 * messages that already have the extension. Returns the number marked.
 */
export async function markThreadRead(medplum: MedplumClient, threadId: string, recipientRef: string): Promise<number> {
  const children = await medplum.searchResources('Communication', [
    ['part-of', `Communication/${threadId}`],
    ['recipient', recipientRef],
    ['_count', '100'],
  ]);
  const unread = children.filter((c) => !isRead(c));
  await Promise.all(unread.map((c) => markNotificationRead(medplum, c)));
  return unread.length;
}
